/**
 * What: Selects the main Shopify embedded-app page body for the active route.
 * Transport / data-flow: Receives already-loaded hooks and route state from ShopifyAppContent; owns no browser history.
 *
 */
import { isFeatureEnabled, type FeatureEntitlementSnapshot } from '@testfeed/entitlement-contracts'
import type { useAppBridge } from '@shopify/app-bridge-react'
import { productLanguage } from '@testfeed/product-language'
import type { ShopifyAppViewState } from './resultsNavigation'
import { CapabilityUnavailablePage } from './components/CapabilityUnavailablePage'
import { InsightsRoute } from './components/insights/InsightsRoute'
import type { TestInsightSetup } from './components/insights/InsightSetupsSection'
import { MicroSurveysPage } from './components/micro-surveys/MicroSurveysPage'
import { ShopifySupportPage } from './components/ShopifySupportPage'
import { WebPixelActivityPage } from './components/web-pixel-activity/WebPixelActivityPage'
export type ShopifyPrimaryContentProps = {
  appView: ShopifyAppViewState
  shopify: ReturnType<typeof useAppBridge>
  entitlements: FeatureEntitlementSnapshot
  onTestSetup?: TestInsightSetup
}

/** Renders the active route's main content for the Shopify app shell. */
export function ShopifyPrimaryContent({ appView, shopify, entitlements, onTestSetup }: ShopifyPrimaryContentProps) {
  const appShellCopy = productLanguage.shopify.components.appShell
  const isMicroSurveysEnabled = isFeatureEnabled(entitlements, 'micro_surveys')
  const isPixelEventsEnabled = isFeatureEnabled(entitlements, 'pixel_events')

  if (appView.view === 'micro-surveys') {
    return isMicroSurveysEnabled ? (
      <MicroSurveysPage shopify={shopify} />
    ) : (
      <CapabilityUnavailablePage
        title={appShellCopy.microSurveysUnavailableTitle}
        message={appShellCopy.microSurveysUnavailableMessage}
      />
    )
  }

  if (appView.view === 'pixel-events') {
    return isPixelEventsEnabled ? (
      <WebPixelActivityPage shopify={shopify} />
    ) : (
      <CapabilityUnavailablePage
        title={appShellCopy.pixelEventsUnavailableTitle}
        message={appShellCopy.pixelEventsUnavailableMessage}
      />
    )
  }

  if (appView.view === 'insights') {
    return <InsightsRoute onTestSetup={onTestSetup} />
  }

  if (appView.view === 'support') {
    return (
      <ShopifySupportPage
        shopify={shopify}
        setupId={appView.setupId}
        packetId={appView.packetId}
        runId={appView.runId}
        latestStepKey={appView.latestStepKey}
      />
    )
  }

  return null
}
